import React, { useEffect, useState } from 'react';
import { Button, Dimmer, Grid, Header, Input, Loader, Modal } from 'semantic-ui-react';

import { FieldLabel } from '../../../../styles';
import AppMaskedInput from '../../../../components/MaskedInput';
import { maskCpfOrCnpj, maskPhoneNumber } from '../../../../services/masks';
import { successNotification, warningNotification } from '../../../../services/notification';
import { updateSegimobPolicyTenant } from '../../../../services/fire-insurance-policy';
import { SectionDivider, SectionHeader, SectionTitle } from './styles';

export type EditPolicyModalData = {
  policyId: string;
  tenantName: string;
  tenantDocument: string;
  tenantEmail: string;
  tenantCellphone: string;
};

type EditPolicyModalProps = {
  open: boolean;
  close: () => void;
  data?: EditPolicyModalData;
  onSuccess: () => void;
};

const EditPolicyModal = ({ open, close, data, onSuccess }: EditPolicyModalProps) => {
  const [tenantName, setTenantName] = useState('');
  const [tenantEmail, setTenantEmail] = useState('');
  const [tenantCellphone, setTenantCellphone] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!data) {
      return;
    }

    setTenantName(data.tenantName ?? '');
    setTenantEmail(data.tenantEmail ?? '');
    setTenantCellphone(data.tenantCellphone ?? '');
  }, [data]);

  const handleClose = () => {
    if (isLoading) {
      return;
    }

    close();
  };

  const handleSave = async () => {
    if (!data?.policyId) {
      return;
    }

    if (!tenantName.trim() || !tenantEmail.trim() || !tenantCellphone.trim()) {
      warningNotification('Opss...', 'Por favor, preencha os campos obrigatórios.');
      return;
    }

    setIsLoading(true);

    try {
      await updateSegimobPolicyTenant(data.policyId, {
        tenantName: tenantName.trim(),
        tenantEmail: tenantEmail.trim(),
        tenantCellphone: tenantCellphone.trim()
      });

      successNotification('Sucesso', 'Apólice atualizada. Nova tentativa de contratação iniciada.');
      onSuccess();
      close();
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeOnEscape={!isLoading}
      closeOnDimmerClick={false}
    >
      <Header icon="edit" content="Editar Apólice" />

      <Modal.Content scrolling>
        <Dimmer active={isLoading} inverted>
          <Loader />
        </Dimmer>

        <SectionHeader>
          <SectionTitle>Dados do inquilino</SectionTitle>
          <SectionDivider />
        </SectionHeader>

        <Grid columns="equal">
          <Grid.Row>
            <Grid.Column width={8}>
              <FieldLabel required>CPF/CNPJ</FieldLabel>
              <Input
                fluid
                disabled
                value={maskCpfOrCnpj(data?.tenantDocument ?? '')}
              />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column>
              <FieldLabel required>Nome completo</FieldLabel>
              <Input
                fluid
                placeholder="Nome completo"
                value={tenantName}
                onChange={(_, inputData) => setTenantName(inputData.value)}
              />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={8}>
              <FieldLabel required>Email</FieldLabel>
              <Input
                fluid
                type="email"
                placeholder="Email"
                value={tenantEmail}
                onChange={(_, inputData) => setTenantEmail(inputData.value)}
              />
            </Grid.Column>

            <Grid.Column width={8}>
              <FieldLabel required>Telefone</FieldLabel>
              <Input
                fluid
                placeholder="Telefone"
                value={maskPhoneNumber(tenantCellphone)}
                onChange={(_, inputData) => setTenantCellphone(inputData.value)}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Modal.Content>

      <Modal.Actions>
        <Button color="red" disabled={isLoading} onClick={handleClose}>
          Cancelar
        </Button>
        <Button color="green" loading={isLoading} disabled={isLoading} onClick={handleSave}>
          Salvar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default EditPolicyModal;
